import { DataFilterExtension, DataFilterExtensionProps } from "@deck.gl/extensions";
import { ScatterplotLayer } from "@deck.gl/layers";
import { useMemo } from "react";

import { ApiGmwGeojsonList200ResponseFeaturesInner } from "@nens/brostar/openapi-client";
import { hexToRgb, removeNullUndefined } from "@nens/shared-utils";

import { useGmnListAllPagesQuery } from "@/queries/query-gmn";
import { useGmwGeojsonQuery } from "@/queries/query-gmw";

import { useGmwContext } from "../../../contexts/gmw-context";
import { GMWSFilters, gmwInformation } from "../../../types";

interface Defaults {
  color: [number, number, number];
  radius: number;
  filterValue: 0 | 1; // 0 = filtered out, 1 = not filtered out
}

interface UseGMW extends Defaults, ApiGmwGeojsonList200ResponseFeaturesInner {}

const defaults: Defaults = {
  color: [255, 140, 0],
  radius: 10,
  filterValue: 1,
};

const passesFilters = (d: UseGMW, filters: GMWSFilters): boolean => {
  const { gmnIds, search, unlinkedChecked } = filters;
  const linkedGmns = d.properties.linkedGmns;

  if ((!linkedGmns || !linkedGmns.length) && unlinkedChecked !== undefined && !unlinkedChecked)
    return false;
  if (linkedGmns && linkedGmns.length && gmnIds) {
    if (!linkedGmns.find((gmn) => gmnIds.includes(gmn))) return false;
  }
  if (search && search.searchString) {
    const key = gmwInformation.find(
      (info) =>
        ("searchKey" in info && info.searchKey === search.searchField) ||
        info.key === search.searchField
    )?.key;
    if (!key) throw new Error("Invalid search key");
    const gmwPart = d.properties[key];
    if (gmwPart !== undefined) {
      if (!gmwPart?.toString().toLowerCase().includes(search.searchString.toString().toLowerCase()))
        return false;
    }
  }
  return true;
};

const useScatterplotLayer = (): ScatterplotLayer => {
  const { selectedGmw, setSelectedGmw, gmwFilters } = useGmwContext();

  const { data } = useGmwGeojsonQuery();
  const { data: gmnData } = useGmnListAllPagesQuery();
  const gmns = useMemo(() => gmnData?.results ?? [], [gmnData]);

  const gmnColors = useMemo(() => {
    const gmnColors: { [key: string]: [number, number, number] } = {};
    gmns.forEach(({ uuid, color }) => {
      if (uuid && color) gmnColors[uuid] = hexToRgb(color);
    });
    return gmnColors;
  }, [gmns]);

  const useGmws = useMemo(
    () =>
      (data?.features ?? []).flatMap((gmw) => {
        const { linkedGmns } = gmw.properties;
        const useGmw: UseGMW = {
          ...gmw,
          ...defaults,
        };
        if (linkedGmns && linkedGmns.length) {
          const actuallyLinkedGmns = removeNullUndefined(
            linkedGmns.filter((gmn) => gmnColors[gmn])
          );

          if (actuallyLinkedGmns.length)
            return actuallyLinkedGmns.map((gmn, index) => ({
              ...useGmw,
              radius: ((linkedGmns.length - index) * defaults.radius) / linkedGmns.length,
              color: gmnColors[gmn],
            }));

          return { ...useGmw, properties: { ...useGmw.properties, linkedGmns: [] } }; // if there are linked GMNs but no colors, treat it as if there are no linked GMNs
        }

        return useGmw;
      }),
    [gmnColors, data]
  );

  const selectedColor = useMemo(
    () =>
      (d: UseGMW): [number, number, number] =>
        selectedGmw && d.properties.uuid === selectedGmw.uuid ? [0, 140, 255] : d.color,
    [selectedGmw]
  );

  return useMemo(
    () =>
      new ScatterplotLayer<UseGMW, DataFilterExtensionProps<UseGMW>>({
        id: "scatterplot-layer",
        data: useGmws,
        pickable: true,
        stroked: true,
        filled: true,
        radiusUnits: "pixels",
        lineWidthUnits: "pixels",

        extensions: [new DataFilterExtension({ filterSize: 1 })],
        filterRange: [1, 1],
        getFilterValue: (d) => (passesFilters(d, gmwFilters) ? 1 : 0),

        getPosition: (d) => d.geometry.coordinates as [number, number],
        getRadius: (d) => d.radius,
        getFillColor: (d) => selectedColor(d),
        getLineColor: (d) => (d.radius === defaults.radius ? [0, 0, 0] : selectedColor(d)),
        getLineWidth: (d) => (d.radius === defaults.radius ? 1 : 0),

        onClick: ({ object }) => {
          setSelectedGmw(object.properties);
        },

        updateTriggers: {
          getFillColor: selectedGmw,
          getLineColor: selectedGmw,
          getFilterValue: gmwFilters,
        },
      }),
    // Disabling exhaustive-deps because setSelectedGmw and selectedColor would cause alot of re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [useGmws, selectedGmw, gmwFilters]
  );
};

export { useScatterplotLayer };
